import React, { forwardRef, useRef, useState, useMemo } from 'react';
import { useSingleEffect, useClickOutside } from 'react-haiku';
import classnames from 'classnames';

import { EditableMathField, StaticMathField } from 'react-mathquill';
import { useSlateStatic, ReactEditor, useReadOnly } from 'slate-react';
import { Transforms } from 'slate';

import Show from 'components/Show';

const Formula = forwardRef(({ element, children, ...attributes }, ref) => {
	// Store input's ref
	const input = useRef(null);

	// Store editor's instance
	const editor = useSlateStatic();

	// Store edior's read only state
	const readOnly = useReadOnly();

	// Store formula's equation
	const [latex, setLatex] = useState('');

	// Generate classnames
	const cls = useMemo(() => classnames(
		'custom-formula',
		latex?.length === 0 && 'custom-formula--empty'
	), [latex]);

	// Handles math field value change
	const onChange = field => {
		setLatex(field.latex());
	};

	// Focus math field on mount if formula is inserted
	const onLoad = field => {
		if (element.focus) field.focus();
	};

	// Update elements parameters on blur
	const onBlur = () => {
		// Get current formula's field path
		const path = ReactEditor.findPath(editor, element);

		// Update formula's data
		Transforms.setNodes(editor, {
			formula: latex,
			focus: false
		}, {
			at: path,
			match: node => node.type === 'formula'
		});
	};

	// Initialize equation on mount
	useSingleEffect(() => setLatex(element.formula));

	// Listen for click outside
	useClickOutside(ref, onBlur);

	return (
		<span
			ref={ref}
			className={cls}
			{...attributes}
			formula={element.formula}
			contentEditable={false}
		>
			{/* Render editable formula */}
			<Show if={!readOnly}>
				<div ref={input}>
					<EditableMathField
						latex={latex}
						onBlur={onBlur}
						onChange={onChange}
						mathquillDidMount={onLoad}
						className="custom-formula-editable"
						config={{
							autoCommands: 'pi theta alpha sqrt sum'
						}}
					/>
				</div>
			</Show>
			
			{/* Render static formula */}
			<Show if={readOnly}>
				<StaticMathField className="custom-formula-rendered">
					{latex}
				</StaticMathField>
			</Show>

			{children}
		</span>
	);
});

export default Formula;
